import { useState } from "react";
import { Gauge, Check, HelpCircle, AlertTriangle, Loader2, Undo2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const barColor = (s) => (s >= 75 ? "bg-brand" : s >= 50 ? "bg-warning" : "bg-danger");

const CategoryRow = ({ c }) => (
  <div data-testid={`compat-category-${c.key}`}>
    <div className="flex items-baseline justify-between gap-3 text-sm mb-1">
      <span className="text-ink">{c.label}</span>
      <span className="font-mono2 text-ink" data-testid={`compat-category-${c.key}-score`}>{c.score}%</span>
    </div>
    <div className="h-1.5 rounded-full bg-canvas overflow-hidden"><div className={`h-full rounded-full ${barColor(c.score)} transition-[width] duration-700`} style={{ width: `${c.score}%` }} /></div>
    {c.reason && <p className="text-xs text-body mt-1.5 leading-relaxed">{c.reason}</p>}
  </div>
);

function MandatoryRow({ m, i, busy, onToggle }) {
  return (
    <div data-testid={`compat-mandatory-${i}`} data-met={m.met} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-line bg-canvas px-3.5 py-2.5">
      <span className={`text-sm ${m.met ? "text-body" : "text-ink"}`}>{m.requirement}</span>
      <span className={`font-mono2 text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.met ? "text-warning bg-warning-soft border-warning/30" : "text-danger bg-danger-soft border-danger/30"}`}>
        {m.met ? "Confirmed by you" : "Not shown on your CV"}
      </span>
      <button data-testid={`compat-mandatory-${i}-toggle`} disabled={busy} onClick={() => onToggle(m)}
        className={`ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${m.met ? "text-body hover:text-ink" : "bg-brand text-onbrand hover:bg-brand-hover"}`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : m.met ? <Undo2 className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
        {m.met ? "Undo" : "I meet this"}
      </button>
    </div>
  );
}

export default function CompatibilityCard({ app, compat, pending, onChange }) {
  const [busy, setBusy] = useState(null);
  const unmet = compat.mandatory.filter((m) => !m.met);

  const toggle = async (m) => {
    setBusy(m.requirement);
    try {
      const { data } = await api.patch(`/applications/${app.id}/compatibility`, { requirement: m.requirement, met: !m.met });
      onChange(data);
      toast.success(m.met ? `"${m.requirement}" marked as not shown` : `Confirmed: ${m.requirement}. Make sure your CV mentions it.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update this requirement");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="compat-card" className="rounded-xl border border-line bg-surface p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-soft border border-brand/30 flex items-center justify-center shrink-0"><Gauge className="w-5 h-5 text-brand" /></div>
          <div>
            <h3 className="font-display text-lg font-bold">Job Compatibility Analysis</h3>
            <p className="text-xs text-faint">How your CV lines up with what this job asks for, and why.</p>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-extrabold text-ink" data-testid="compat-overall">{compat.overall}%</div>
          {compat.capped && <div className="text-xs text-danger" data-testid="compat-capped">capped from {compat.raw}%</div>}
        </div>
      </div>

      {unmet.length > 0 && (
        <div className="mb-5 rounded-lg border border-danger/30 bg-danger-soft p-4 text-sm text-body flex gap-3" data-testid="compat-cap-notice">
          <AlertTriangle className="w-4 h-4 text-danger mt-0.5 shrink-0" />
          <span>This job lists {unmet.length === 1 ? "a mandatory requirement" : `${unmet.length} mandatory requirements`} your CV doesn't show, so the overall score is capped at {compat.cap}%. If you do meet it, confirm below and make sure your CV says so.</span>
        </div>
      )}

      <div className="space-y-4">{compat.categories.map((c) => <CategoryRow key={c.key} c={c} />)}</div>

      {compat.mandatory.length > 0 && (
        <div className="mt-6" data-testid="compat-mandatory-list">
          <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-2">Mandatory requirements</div>
          <div className="space-y-2">{compat.mandatory.map((m, i) => <MandatoryRow key={m.requirement} m={m} i={i} busy={busy === m.requirement} onToggle={toggle} />)}</div>
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-canvas border border-line p-3.5" data-testid="compat-strengths">
          <div className="text-success flex items-center gap-1.5 font-semibold"><Check className="w-4 h-4" aria-hidden="true" /> Why it fits</div>
          <ul className="mt-1.5 space-y-1 text-xs text-body leading-relaxed">
            {(compat.strengths.length ? compat.strengths : ["Review the breakdown above for where your CV lines up."]).map((s) => <li key={s}>{s}</li>)}
          </ul>
        </div>
        <button data-testid="compat-needs-input" onClick={() => document.getElementById("evidence-checker")?.scrollIntoView({ behavior: "smooth", block: "center" })}
          className="text-left rounded-lg bg-canvas border border-line p-3.5 hover:border-info/40 transition-colors">
          <div className="text-info flex items-center gap-1.5 font-medium"><HelpCircle className="w-4 h-4" /> Needs your input</div>
          <p className="mt-1.5 text-xs text-body leading-relaxed">
            {pending ? `${pending} job requirement${pending === 1 ? "" : "s"} in the Evidence Checker need${pending === 1 ? "s" : ""} your answer.` : "Nothing waiting. Every skill gap has been reviewed."}
          </p>
        </button>
      </div>

      <p className="mt-5 text-xs text-faint leading-relaxed flex gap-2" data-testid="compat-disclaimer">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        This score is GetInterviewed's own estimate, not how any employer's ATS ranks candidates. A high score doesn't guarantee an interview.
      </p>
    </div>
  );
}
